import { useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DeviceDetail } from '@kirin/api-client/contracts/device-inventory';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useToast } from '@/hooks/use-toast';
import { resetDevice, revokeDevice } from './api';
import { deviceDisplayName } from './deviceDisplay';

type Action = 'revoke' | 'reset';

const COPY: Record<
  Action,
  {
    button: string;
    title: (name: string) => string;
    description: string;
    confirm: string;
    confirming: string;
    done: (name: string) => string;
  }
> = {
  revoke: {
    button: 'Revoke device',
    title: name => `Revoke ${name}?`,
    description:
      'Kirin stops accepting the device, and everyone who signed in on it, from this device at once. The device cannot enroll again until you reset it.',
    confirm: 'Revoke device',
    confirming: 'Revoking...',
    done: name => `${name} is revoked. Its keys no longer work.`,
  },
  reset: {
    button: 'Reset device',
    title: name => `Reset ${name}?`,
    description:
      'The device can enroll again, for example after it was reimaged. Then run your MDM install script on it again: the script re-enrolls the device. Its old keys stay revoked; enrolling issues new ones.',
    confirm: 'Reset device',
    confirming: 'Resetting...',
    done: name => `${name} can enroll again.`,
  },
};

/**
 * KIR-5658 — "Revoke device" for an enrolled machine, "Reset device" for a
 * revoked one. Owners and Managers only (the caller decides); each asks for
 * confirmation in the page first.
 */
export function DeviceRevocationActions({
  organizationId,
  device,
}: {
  organizationId: string;
  device: DeviceDetail;
}): ReactElement | null {
  const [confirming, setConfirming] = useState<Action | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const name = deviceDisplayName(device);

  const mutation = useMutation({
    mutationFn: (action: Action) =>
      action === 'revoke'
        ? revokeDevice(organizationId, device.id)
        : resetDevice(organizationId, device.id),
    onSuccess: async (_state, action) => {
      toast({
        title: action === 'revoke' ? 'Device revoked' : 'Device reset',
        description: COPY[action].done(name),
        variant: 'success',
      });
      setConfirming(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.devices.all });
    },
    onError: (error, action) => {
      toast({
        title:
          action === 'revoke'
            ? "Couldn't revoke the device"
            : "Couldn't reset the device",
        description:
          error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  const action: Action | null = device.revokedAt
    ? 'reset'
    : device.enrolled
      ? 'revoke'
      : null;
  if (!action) return null;
  const copy = COPY[action];

  return (
    <>
      <Button
        type="button"
        variant={action === 'revoke' ? 'destructive' : 'outline'}
        size="sm"
        onClick={() => setConfirming(action)}
      >
        {copy.button}
      </Button>
      <ConfirmationDialog
        isOpen={confirming !== null}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && mutation.mutate(confirming)}
        title={copy.title(name)}
        description={copy.description}
        confirmLabel={copy.confirm}
        confirmingLabel={copy.confirming}
        isLoading={mutation.isPending}
        variant={action === 'revoke' ? 'destructive' : 'default'}
      />
    </>
  );
}

/** KIR-5658: what a revoked machine means, shown at the top of its page. */
export function RevokedNotice({
  revokedAt,
}: {
  revokedAt: string;
}): ReactElement {
  return (
    <p
      role="status"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
    >
      This endpoint was revoked{' '}
      <RelativeTimeWithTooltip date={revokedAt} inline />. Its keys no longer
      work, and it cannot enroll again until it is reset.
    </p>
  );
}
